import environment from "@/modules/environment";
import { addTimeout } from "@/utils";
import { DB_TIMEOUT } from "camera/lib/Recorder/const";
import { MediaUploadManager } from "camera/modules/upload/MediaUploadManager";

const CHECK_INTERVAL = 60 * 1000; // 1 minute
const MAX_TIME_WITHOUT_ANSWER = 10 * 60 * 1000; // 10 minutes
const MIN_TIME_BETWEEN_RESTARTS = 60 * 60 * 1000; // 1 hour
const LAST_RESTART_KEY = environment.appMode + "-db-recovery-restart";

// Restarts the app when the recording DB doesn't answer for a long time, a reload frees a stuck IndexedDB.
// Only when the restart loses nothing: no segments that exist only in memory and no event being recorded.
export default class DbRecovery {
  private static recorder: HlsRecorder | null = null;
  private static lastAnswerTime = Date.now();

  static init = (recorderCreator: RecorderCreator) => {
    const unsub = recorderCreator.onRecorderInit((recorder) => {
      unsub();
      this.recorder = recorder;
      this.lastAnswerTime = Date.now();
      setInterval(this.check, CHECK_INTERVAL);
    });
  };

  private static check = async () => {
    if (!this.recorder) return;
    const db = this.recorder.db;
    const request = db.isOpen() ? () => db.probeWrite("hls-data") : db.init;
    const isAnswering = await addTimeout(request, DB_TIMEOUT)
      .then(() => true)
      .catch(() => false);
    if (isAnswering) {
      this.lastAnswerTime = Date.now();
      return;
    }

    const timeWithoutAnswer = Date.now() - this.lastAnswerTime;
    const minutesWithoutAnswer = Math.floor(timeWithoutAnswer / 60000);
    log.warn(`Recording DB hasn't answered for ${minutesWithoutAnswer} min`);
    if (timeWithoutAnswer < MAX_TIME_WITHOUT_ANSWER) return;

    if (this.wouldRestartLoseUploads()) {
      log.warn("Not restarting the app yet, it would lose segments waiting for upload");
      return;
    }
    if (this.hasRestartedRecently()) {
      log.warn("Not restarting the app, it was restarted for the same reason less than an hour ago");
      return;
    }

    log.err(`Recording DB hasn't answered for ${minutesWithoutAnswer} min, restarting the app`);
    localStorage.setItem(LAST_RESTART_KEY, String(Date.now()));
    if (!isDev) restartApp({ useLimiter: true, cause: "db-not-answering" });
  };

  private static wouldRestartLoseUploads = () =>
    MediaUploadManager.hasMemoryOnlySegments() || Boolean(this.recorder?.isMarkingDataForUpload());

  private static hasRestartedRecently = () => {
    const lastRestartTime = Number(localStorage.getItem(LAST_RESTART_KEY));
    return Date.now() - lastRestartTime < MIN_TIME_BETWEEN_RESTARTS;
  };
}
